import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus, Trash2, KeyRound, ShieldCheck, UserRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", password: "", role: "staff" };
const errToast = (e, fb) => toast.error(formatApiError(e.response?.data?.detail, fb));

function UserRow({ u, me, onToggle, onResetPw, onDelete, onRole, onHR }) {
  const isMe = u.id === me.id;
  return (
    <div data-testid={`team-user-${u.id}`} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_auto_auto_auto] gap-3 items-center rounded-lg border border-slate-800 bg-slate-950/30 p-3">
      <div className="min-w-0">
        <div className="font-semibold text-sm truncate flex items-center gap-1.5">
          {u.role === "owner" ? <ShieldCheck className="w-3.5 h-3.5 text-amber-400 shrink-0" /> : <UserRound className="w-3.5 h-3.5 text-slate-500 shrink-0" />}
          {u.name || u.email}{isMe && <span className="text-[10px] uppercase tracking-wider text-amber-300">· you</span>}
        </div>
        <div className="text-xs text-slate-500 truncate">{u.email}</div>
      </div>
      <button onClick={() => onRole(u)} disabled={isMe} data-testid={`team-role-${u.id}`} className="text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md border border-slate-700 text-slate-300 hover:border-amber-400/60 disabled:opacity-50">{u.role}</button>
      <div className="flex flex-col gap-1">
        <label className="flex items-center gap-2 text-xs text-slate-400"><Switch checked={u.active} disabled={isMe} onCheckedChange={(v) => onToggle(u, v)} data-testid={`team-active-${u.id}`} /> {u.active ? "Active" : "Disabled"}</label>
        <label className="flex items-center gap-2 text-xs text-slate-400" title="Grant the HR module without changing the role"><Switch checked={!!u.hr_access} disabled={u.role === "owner" || u.role === "hr"} onCheckedChange={(v) => onHR(u, v)} data-testid={`team-hr-${u.id}`} /> HR access</label>
      </div>
      <div className="flex gap-1 justify-end col-span-2 sm:col-span-1">
        <button onClick={() => onResetPw(u)} title="Reset password" data-testid={`team-resetpw-${u.id}`} className="h-8 w-8 grid place-items-center rounded-md text-slate-500 hover:text-amber-300 hover:bg-slate-800"><KeyRound className="w-4 h-4" /></button>
        <button onClick={() => onDelete(u)} disabled={isMe} title="Remove" data-testid={`team-delete-${u.id}`} className="h-8 w-8 grid place-items-center rounded-md text-slate-500 hover:text-rose-400 hover:bg-slate-800 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export default function TeamDialog({ open, onOpenChange }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => axios.get(`${API}/users`).then(({ data }) => setUsers(data)).catch(() => {}), []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const patch = async (u, body, msg) => {
    try { await axios.patch(`${API}/users/${u.id}`, body); toast.success(msg); load(); } catch (e) { errToast(e, "Update failed"); }
  };

  const add = async () => {
    if (!form.email || form.password.length < 6) return toast.error("Email and a password of 6+ characters are required");
    try {
      await axios.post(`${API}/users`, form);
      toast.success("Staff account created", { description: form.email });
      setForm(EMPTY);
      load();
    } catch (e) { errToast(e, "Could not create user"); }
  };

  const resetPw = (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 6 characters):`);
    if (pw) patch(u, { password: pw }, "Password updated");
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? They will no longer be able to sign in.`)) return;
    try { await axios.delete(`${API}/users/${u.id}`); toast.success("User removed"); load(); } catch (e) { errToast(e, "Delete failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#111827] border-slate-700 text-slate-100 max-h-[90vh] overflow-y-auto" data-testid="team-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold">Team & Access <span className="font-zh text-amber-300/80 text-lg">员工账户</span></DialogTitle>
          <DialogDescription className="text-slate-400">Staff can use everything; Accounts can confirm payments; HR gets the HR module; only Owners manage prices and accounts. Tap a role to cycle it.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">{users.map((u) => (
          <UserRow key={u.id} u={u} me={me} onToggle={(x, v) => patch(x, { active: v }, v ? "Account enabled" : "Account disabled")}
            onRole={(x) => patch(x, { role: { staff: "accounts", accounts: "hr", hr: "owner", owner: "staff" }[x.role] || "staff" }, "Role updated")} onHR={(x, v) => patch(x, { hr_access: v }, v ? "HR access granted" : "HR access removed")} onResetPw={resetPw} onDelete={remove} />
        ))}</div>

        <div className="rounded-xl border border-dashed border-slate-700 bg-slate-950/30 p-4 space-y-3">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5" /> Add staff account</p>
          <div className="grid sm:grid-cols-2 gap-2">
            <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-new-name" className="bg-slate-950/60 border-slate-700 h-9 text-sm" />
            <Input type="email" placeholder="email@hupleyhuat.sg" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-new-email" className="bg-slate-950/60 border-slate-700 h-9 text-sm" />
            <Input type="text" placeholder="Temporary password (6+)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="team-new-password" className="bg-slate-950/60 border-slate-700 h-9 text-sm font-mono-num" />
            <div className="flex gap-2">
              <div className="flex rounded-lg border border-slate-700 bg-slate-950/60 p-0.5 flex-1">
                {["staff", "accounts", "hr", "owner"].map((r) => (
                  <button key={r} data-on={form.role === r} onClick={() => setForm({ ...form, role: r })} data-testid={`team-new-role-${r}`} className="flex-1 px-2 py-1 rounded-md text-xs font-semibold capitalize text-slate-400 data-[on=true]:bg-amber-400 data-[on=true]:text-slate-950 transition-colors">{r}</button>
                ))}
              </div>
              <Button onClick={add} data-testid="team-add-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold h-9"><UserPlus className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
